import { MOTION, RADIUS, SHADOW } from "@elyxeer/ui";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import { ArticleCover } from "~/components/news/ArticleCover";
import { SectionHeading } from "~/components/ui/SectionHeading";
import { fetchContinue } from "~/lib/api";
import { queryKeys } from "~/lib/query-client";
import type { ContinueItem } from "~/types/progress";

/** The query string that asks the article to scroll back to where the reader stopped. */
export const RESUME_PARAM = "resume";

function formatPosition(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = Math.floor(seconds % 60);
  return `${minutes}:${String(rest).padStart(2, "0")}`;
}

/**
 * How far along an item is, as a fraction for the bar and a sentence for the label.
 *
 * A video's position is seconds, not a fraction — the duration is on the article, and
 * only when the editor filled it in — so the bar is left indeterminate for a video
 * without one and the label says the time instead.
 */
function describe(
  item: ContinueItem,
  t: (key: string, options?: Record<string, unknown>) => string,
): { fraction: number | null; label: string } {
  const { progress } = item;
  if (
    progress.video_position_seconds !== null &&
    progress.video_position_seconds > 0
  ) {
    return {
      fraction: null,
      label: t("continue.video", {
        position: formatPosition(progress.video_position_seconds),
      }),
    };
  }
  const fraction = progress.scroll_fraction ?? 0;
  return {
    fraction,
    label: t("continue.reading", { percent: Math.round(fraction * 100) }),
  };
}

function ContinueCard({ item }: { item: ContinueItem }) {
  const { t } = useTranslation();
  const { fraction, label } = describe(item, t);

  return (
    <Card
      sx={{
        minWidth: 0,
        transition: `box-shadow ${MOTION.transition}, transform ${MOTION.transition}`,
        "&:hover": { boxShadow: SHADOW.raised, transform: "translateY(-2px)" },
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={`/news/${item.article.slug}?${RESUME_PARAM}=1`}
        data-testid={`continue.card-${item.article.slug}`}
        sx={{ display: "flex", alignItems: "stretch", height: "100%" }}
      >
        <ArticleCover
          slug={item.article.slug}
          src={item.article.cover_image_url}
          alt=""
          sx={{
            width: 128,
            flexShrink: 0,
            alignSelf: "center",
            borderRadius: 0,
          }}
        />
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            padding: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            justifyContent: "center",
          }}
        >
          <Typography
            variant="L1"
            sx={{
              color: "text.primary",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {item.article.title}
          </Typography>
          {fraction !== null ? (
            <LinearProgress
              variant="determinate"
              value={Math.round(fraction * 100)}
              sx={{ borderRadius: `${RADIUS.full}px`, height: 4 }}
            />
          ) : null}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              color: "link.main",
            }}
          >
            <PlayArrowIcon sx={{ fontSize: "1rem" }} />
            <Typography variant="L3" sx={{ color: "inherit" }}>
              {label}
            </Typography>
          </Box>
        </Box>
      </CardActionArea>
    </Card>
  );
}

/**
 * "Pokračovat" on the dashboard: the articles and videos the member left unfinished,
 * most recently touched first. Renders nothing when there is nothing to pick up — a
 * heading over an empty row would look like a loading failure.
 */
export function ContinueSection({
  userId,
  locale,
}: {
  userId: string;
  locale: string;
}) {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: queryKeys.continueList(userId, locale),
    queryFn: ({ signal }) => fetchContinue(locale, signal),
    refetchOnMount: "always",
  });

  if (!data || data.items.length === 0) return null;

  return (
    <Box
      data-testid="continue.section"
      sx={{ display: "flex", flexDirection: "column", gap: { xs: 2, md: 3 } }}
    >
      <SectionHeading>{t("continue.heading")}</SectionHeading>
      <Box
        role="list"
        aria-label={t("continue.list")}
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            md: "repeat(auto-fill, minmax(320px, 1fr))",
          },
          gap: { xs: 1.5, md: 2 },
        }}
      >
        {data.items.map((item) => (
          <Box role="listitem" key={item.article.id}>
            <ContinueCard item={item} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}
